import React, { Fragment, type JSX } from 'react';

import { getL10nNumber } from '@travel/i18n';
import { Bed, ConciergeService, HotSpring, MealsIncluded, Restaurant } from '@travel/icons/service';
import { LoungeEquipment, MapOutline, ShineMagic, StarGray } from '@travel/icons/ui';
import { Translate } from '@travel/translation';
import { StarRatingText } from '@travel/ui';

import CustomReviewBar from 'components/CustomReviewBar';

import { LANGUAGE_CODE, NO_SCORE_PLACEHOLDER } from 'constants/common';
import { CATEGORY_SCORE } from './label';

import styles from './scoresSection.module.scss';

type rating = {
  stars: number;
  count: number;
};

type categoryRating = {
  code: string;
  average: number;
};

export type Props = {
  overallScore: number;
  reviewCount: number;
  starRating: rating[];
  categoryRating: categoryRating[];
};

const categoryIcons: Record<string, JSX.Element> = {
  ROOM: <Bed size={22} color="sonicSilver" />,
  HOSPITALITY: <ConciergeService size={22} color="sonicSilver" />,
  LOCATION: <MapOutline size={22} color="sonicSilver" />,
  CLEANLINESS: <ShineMagic size={22} color="sonicSilver" />,
  BATH: <HotSpring size={22} color="sonicSilver" />,
  FACILITY: <LoungeEquipment size={22} color="sonicSilver" />,
  BREAKFAST: <MealsIncluded size={22} color="sonicSilver" />,
  DINNER: <Restaurant size={22} color="sonicSilver" />,
};

const ScoresSection = ({ overallScore, reviewCount, starRating, categoryRating }: Props) => {
  const formattedReview = (
    <>
      {getL10nNumber(LANGUAGE_CODE, reviewCount)}
      <Translate id="Review_Common.ReviewCount" />
    </>
  );

  const maxReviewCount = starRating.reduce((max, rating) => Math.max(max, rating.count), 0);

  return (
    <div data-testid="category-score-container" className={styles.overallReviewWrapper}>
      <div className={styles.firstSection}>
        <div className={styles.overallScoreSection} data-testid="score-section">
          <div className={styles.scoreTitle}>
            <Translate id="PostReview.Form.OverallRating.Title" />
          </div>
          <div className={styles.overallScore}>
            {overallScore > 0 ? overallScore.toFixed(2) : NO_SCORE_PLACEHOLDER}
          </div>
          <StarRatingText
            displayGrayStar={overallScore === 0}
            className={styles.averageStarRating}
            isDisplayRating={false}
            iconSize={20}
            ratingValue={overallScore}
          />
          <div className={styles.totalReviewCount} data-testid="overall-review-count">
            {formattedReview}
          </div>
        </div>
        <div className={styles.scoreDistributionSection} data-testid="star-rating-section">
          {starRating &&
            starRating.map((rating) => (
              <Fragment key={rating.stars}>
                <div className={styles.ratingStars}>
                  <StarGray color={'cloudyGray'} className={styles.starIcon} size={14} />
                  <span className={styles.starValue}>{rating.stars}</span>
                </div>
                <div className={styles.ratingBarBackground}>
                  <CustomReviewBar score={rating.count} max={maxReviewCount} hasAnimation={true} />
                </div>
                <div className={styles.ratingCount}>
                  <>
                    {getL10nNumber(LANGUAGE_CODE, rating.count)}
                    <Translate id="Review_Common.ReviewCount" />
                  </>
                </div>
              </Fragment>
            ))}
        </div>
        <div className={styles.categoryScore} data-testid="category-score-section">
          {categoryRating.map((category) => (
            <div key={category.code} className={styles.categoryBox}>
              {categoryIcons[category.code]}
              <div className={styles.categoryCode}>{CATEGORY_SCORE[category.code]}</div>
              <div className={styles.categoryAverage} data-test-id="category-score">
                {category.average > 0 ? category.average.toFixed(2) : NO_SCORE_PLACEHOLDER}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ScoresSection;
